import { createContext, useEffect, useState } from 'react';

/**
 * The labels for a given media query.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The context for the viewport size, either 'narrow' when the mobile
 * menu is visible, or 'wide' for viewport widths above that point.
 */
export const ViewportContext = /*#__PURE__*/createContext(null);

/**
 * A provider used _only_ to conditionally render components. Typically,
 * for performance reasons, you should use `mediaQueries` within a
 * styled component when a component's CSS properties change
 * across breakpoints but there is no need to conditionally render it or move
 * the HTML/markup.
 *
 * Pass in `queries` for `isMobile` and `isDesktop` to test whether each query
 * matches. Then, use the `useViewport` hook to check those values in descendant
 * components.
 *
 * @example
 * ```tsx
 * <ViewportProvider
 *   queries={{
 *     isMobile: `(max-width: 1023px)`,
 *     isDesktop: `(min-width: 1024px)`,
 *     isBelowLaptop: `(max-width: 1439px)`,
 *   }}
 * >
 *   children here
 * </ViewportProvider>
 * ```
 *
 * @see https://private.hubteamqa.com/developer-docs-shared-components-storybook/?path=/story/utils-media-queries-and-breakpoints--page
 *
 */
export const ViewportProvider = ({
  children,
  queries
}) => {
  const [queryMatch, setQueryMatch] = useState({});
  useEffect(() => {
    const mediaQueryLists = {};
    const keys = Object.keys(queries);
    let isAttached = false;
    const handleQueryListener = () => {
      const updatedMatches = keys.reduce((acc, media) => {
        acc[media] = !!(mediaQueryLists[media] && mediaQueryLists[media].matches);
        return acc;
      }, {});
      setQueryMatch(updatedMatches);
    };
    if (window && window.matchMedia) {
      const matches = {};
      keys.forEach(media => {
        mediaQueryLists[media] = window.matchMedia(queries[media]);
        matches[media] = mediaQueryLists[media].matches;
      });
      setQueryMatch(matches);
      isAttached = true;
      keys.forEach(media => {
        mediaQueryLists[media].addEventListener('change', handleQueryListener);
      });
    }
    return () => {
      if (isAttached) {
        keys.forEach(media => {
          mediaQueryLists[media].removeEventListener('change', handleQueryListener);
        });
      }
    };
  }, [queries]);
  return /*#__PURE__*/_jsx(ViewportContext.Provider, {
    value: queryMatch,
    children: children
  });
};